import { useState, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import { EntityPageLayout } from '@/components/common';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from '@/components/ui/drawer';
import { useIsMobile } from '@/hooks/use-mobile';
import { PermissionGate } from '@/components/auth/PermissionGate';
import { useAuth } from '@/hooks/useAuth';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { PromoOffer } from '../types';

// Feature components
import { 
  CreatePromoOfferForm, 
  EditPromoOfferForm, 
  PromoOfferTable,
  PromoOfferDetails 
} from '../components';
import { 
  usePromoOffers, 
  useCreatePromoOffer 
} from '../hooks/usePromoOfferManagement';
import { type CreatePromoOfferInput } from '../schemas/promoOfferSchemas';

export function PromoOffersPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [detailsDrawer, setDetailsDrawer] = useState<{
    open: boolean;
    promoOffer: PromoOffer | null;
  }>({ open: false, promoOffer: null });
  const isMobile = useIsMobile();
  const { hasPermission } = useAuth();

  // React Query hooks
  const { data, isLoading, error } = usePromoOffers();
  const createPromoOfferMutation = useCreatePromoOffer();

  const promoOffers = data?.promo_offers || [];
  const totalCount = data?.total_count || 0;
  const activeCount = data?.active_promo_offers_count || 0;
  const inactiveCount = data?.inactive_promo_offers_count || 0;
  const totalClaims = data?.total_alltime_claimed || 0;

  // Handle /new route
  useEffect(() => {
    if (location.pathname === '/promo-offers/new') {
      if (hasPermission('promo_management:create')) {
        setIsCreateDrawerOpen(true);
      } else {
        // Redirect to main page if no permission
        navigate('/promo-offers');
      }
    }
  }, [location.pathname, hasPermission, navigate]);

  // Handle drawer close - navigate back to main page
  const handleCreateDrawerChange = (open: boolean) => {
    setIsCreateDrawerOpen(open);
    if (!open && location.pathname === '/promo-offers/new') {
      navigate('/promo-offers');
    }
  };

  // Handle create promo offer
  const handleCreatePromoOffer = async (values: CreatePromoOfferInput) => {
    try {
      await createPromoOfferMutation.mutateAsync(values);
      handleCreateDrawerChange(false);
    } catch {
      // Error is handled by the mutation hook
    }
  };

  // Handle view details
  const handleViewDetails = useCallback((promoOffer: PromoOffer) => {
    setDetailsDrawer({ open: true, promoOffer });
  }, []);

  // Render edit form for the drawer
  const renderEditForm = useCallback((promoOffer: PromoOffer, onClose: () => void) => {
    return <EditPromoOfferForm promoOffer={promoOffer} onClose={onClose} formId="edit-form" />;
  }, []);

  return (
    <>
      <Helmet>
        <title>Promo Offers - Urban Finch Admin</title>
      </Helmet>
      <EntityPageLayout
        title="Promo Offers"
        description="Create and manage promotional offers with promo codes"
        permission="promo_management:read"
        testIdPrefix="promo-offers"
        isLoading={isLoading}
        error={
          error instanceof Error
            ? error
            : error
              ? new Error(String(error))
              : undefined
        }
        headerActions={
          <PermissionGate permission="promo_management:create">
            <Button
              onClick={() => handleCreateDrawerChange(true)}
              data-testid="create-promo-offer-button"
            >
              Create Promo Offer
            </Button>
          </PermissionGate>
        }
      >
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Promo Offers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className="text-2xl font-bold"
                  data-testid="promo-offer-total-count"
                >
                  {totalCount}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Active Promo Offers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className="text-2xl font-bold"
                  data-testid="promo-offer-active-count"
                >
                  {activeCount}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Claims
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className="text-2xl font-bold"
                  data-testid="promo-offer-total-claims"
                >
                  {totalClaims}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Inactive Promo Offers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className="text-2xl font-bold"
                  data-testid="promo-offer-inactive-count"
                >
                  {inactiveCount}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Promo Offers Table with Search */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>All Promo Offers</CardTitle>
                  <CardDescription>
                    Manage promotional offers with promo codes for venues
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <PromoOfferTable
                data={promoOffers}
                isLoading={isLoading}
                error={error instanceof Error ? error : null}
                renderEditForm={
                  hasPermission('promo_management:update')
                    ? renderEditForm
                    : undefined
                }
                onViewDetails={handleViewDetails}
              />
            </CardContent>
          </Card>
        </div>

        {/* Create Promo Offer Drawer */}
        <Drawer
          open={isCreateDrawerOpen}
          onOpenChange={handleCreateDrawerChange}
          direction={isMobile ? 'bottom' : 'right'}
        >
          <DrawerContent className={isMobile ? 'h-[90vh]' : 'w-[500px] h-full'}>
            <DrawerHeader>
              <DrawerTitle>Create New Promo Offer</DrawerTitle>
              <DrawerDescription>Add a new promotional offer with promo code</DrawerDescription>
            </DrawerHeader>
            <div className="px-4 flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
              <CreatePromoOfferForm
                onSubmit={handleCreatePromoOffer}
                formId="create-promo-offer-form"
              />
            </div>
            <DrawerFooter>
              <Button
                type="submit"
                form="create-promo-offer-form"
                disabled={createPromoOfferMutation.isPending}
                data-testid="promo-offer-form-submit"
              >
                {createPromoOfferMutation.isPending ? 'Creating...' : 'Create Promo Offer'}
              </Button>
              <Button
                variant="outline"
                onClick={() => handleCreateDrawerChange(false)}
                disabled={createPromoOfferMutation.isPending}
                data-testid="promo-offer-form-cancel"
              >
                Cancel
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>

        {/* View Details Drawer */}
        <Drawer
          open={detailsDrawer.open}
          onOpenChange={(open) => setDetailsDrawer({ open, promoOffer: detailsDrawer.promoOffer })}
          direction={isMobile ? 'bottom' : 'right'}
        >
          <DrawerContent className={isMobile ? 'h-[90vh]' : 'w-[600px] h-full'}>
            <DrawerHeader>
              <DrawerTitle>Promo Offer Details</DrawerTitle>
              <DrawerDescription>View promo offer information and statistics</DrawerDescription>
            </DrawerHeader>
            <div className="px-4 flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
              {detailsDrawer.promoOffer && (
                <PromoOfferDetails promoOffer={detailsDrawer.promoOffer} />
              )}
            </div>
            <DrawerFooter>
              <Button
                variant="outline"
                onClick={() => setDetailsDrawer({ open: false, promoOffer: null })}
              >
                Close
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </EntityPageLayout>
    </>
  );
}
